// ============================================================
// woodSKUs.js
// SKU mapping for the Wood fence estimator.
// Pure data only — no prices here. Prices live in the Price List
// (Firestore) and are resolved by SKU in woodCalc.js (built next).
// Product: pressure-treated wood fence in 4', 5', 6' and 8' heights,
// plus the Paddock and Lattice variants.
//
// SHARED SKUs — several part numbers fill more than one role, so the
// same string intentionally appears under several exports below:
//   '148PT'  = 1x4x8 picket (8' fence) AND cap board AND fascia board
//              AND the 2-per-section lattice boards
//   '168PT'  = 1x6x8 picket (8' 1x6 fence) AND the Paddock horizontal board
//   '144PT'  = 1x4x4 picket (4' fence) AND the 4' Paddock faceboard
//   '145PT'  = 1x4x5 picket (5' fence) AND the 5' Paddock faceboard
//              AND the forced picket on Lattice jobs
// Duplicating them per role keeps woodCalc.js readable: the engine asks
// for the role it needs and never has to know two roles share a part.
//
// Height-keyed maps use STRING keys ('4' | '5' | '6' | '8') to match the
// width-keyed map convention in vinylSKUs.js.
// ============================================================

// --- Posts (by fence height) ---
// Regular line/corner/end posts. 4x4 stock.
export const WOOD_REGULAR_POST_SKU = {
  '4': '4407', // 4x4x7 — 4' fence
  '5': '4408', // 4x4x8 — 5' fence
  '6': '4410', // 4x4x10 — 6' fence
  '8': '4412', // 4x4x12 — 8' fence
};

// Gate posts. 6x6 stock. NOTE: 4' and 5' share the same post ('6608').
export const WOOD_GATE_POST_SKU = {
  '4': '6608', // 6x6x8 — 4' fence
  '5': '6608', // 6x6x8 — 5' fence (same as 4')
  '6': '6610', // 6x6x10 — 6' fence
  '8': '6612', // 6x6x12 — 8' fence
};

// --- Post Caps (1 per post) ---
// One cap per family, used at every height.
export const WOOD_REGULAR_POST_CAP_SKU = 'PC44'; // Post Cap 4x4 — all regular posts
export const WOOD_GATE_POST_CAP_SKU = 'PC66';    // Post Cap 6x6 — all gate posts

// --- Boards / Pickets (full catalogue, keyed by nominal dimension) ---
// Canonical list of every board SKU. The role-specific exports below point
// back into these same part numbers.
export const WOOD_BOARD_SKU = {
  '1x4x4': '144PT',   // 1x4x4 pressure treated
  '1x4x5': '145PT',   // 1x4x5 pressure treated
  '1x4x6': '146PT',   // 1x4x6 pressure treated
  '1x4x8': '148PT',   // 1x4x8 pressure treated
  '1x6x6': '166PT',   // 1x6x6 pressure treated
  '1x6x8': '168PT',   // 1x6x8 pressure treated
  '1x6x6DogEar': '166PTDE', // 5/8x6x6 dog ear pressure treated
};

// Pickets by fence height — 1x4 profile.
export const WOOD_PICKET_1X4_SKU = {
  '4': '144PT', // also the 4' Paddock faceboard
  '5': '145PT', // also the 5' Paddock faceboard, and the forced Lattice picket
  '6': '146PT',
  '8': '148PT', // also cap board / fascia / lattice board
};

// Pickets by fence height — 1x6 profile. Only stocked at 6' and 8'.
export const WOOD_PICKET_1X6_SKU = {
  '6': '166PT',
  '8': '168PT', // also the Paddock horizontal board
};

// Dog ear picket — single size (5/8x6x6).
export const WOOD_DOG_EAR_PICKET_SKU = '166PTDE';

// --- Trim boards ---
// Both are the 1x4x8 board; separate exports so the engine can ask by role.
export const WOOD_CAP_BOARD_SKU = '148PT';    // cap board (top of fence)
export const WOOD_FASCIA_BOARD_SKU = '148PT'; // fascia board

// --- Framing ---
export const WOOD_RAIL_SKU = '2408PT';       // 2x4x8 PT — fence rails / framing
export const WOOD_GATE_FRAME_SKU = '248WRC'; // 2x4x8 Western Red Cedar — gate framing

// --- Gate Hardware ---
// T-hinges, sold per pair. Keyed by hinge size in inches.
export const WOOD_GATE_HINGE_SKU = {
  '8': '8TH',   // T-Hinge 8" (pair) (DEFAULT)
  '10': '10TH', // T-Hinge 10" (pair)
};

// Latches, keyed by type.
export const WOOD_LATCH_SKU = {
  gravity: 'HDLNS', // Gravity latch (DEFAULT for 4' fences)
  thumb: 'TL',      // Thumb latch (DEFAULT for 5' / 6' / 8' fences)
};

// Which latch type each fence height defaults to. Values are keys of
// WOOD_LATCH_SKU above — the user can still override the choice.
export const WOOD_DEFAULT_LATCH_BY_HEIGHT = {
  '4': 'gravity',
  '5': 'thumb',
  '6': 'thumb',
  '8': 'thumb',
};

// Drop rod / cane bolt — double gate pairs.
export const WOOD_DROP_ROD_SKU = 'CB2';

// --- Paddock ---
// Welded wire backing, keyed by FENCE height (the roll height differs).
export const WOOD_WELDED_WIRE_SKU = {
  '4': 'WWB244814', // welded wire 48" high — 4' fences
  '5': 'WWB246014', // welded wire 60" high — 5' fences
};

// Paddock faceboards — same parts as the 1x4 pickets at those heights.
export const WOOD_PADDOCK_FACEBOARD_SKU = {
  '4': '144PT', // = 1x4x4
  '5': '145PT', // = 1x4x5
};

// Paddock horizontal board — same part as the 8' 1x6 picket.
export const WOOD_PADDOCK_HORIZONTAL_BOARD_SKU = '168PT'; // = 1x6x8

// Paddock gate pickets — the arched paddock gate uses pickets ONE FOOT TALLER
// than the fence (a 4' fence gate uses 1x4x5, a 5' fence gate uses 1x4x6).
// Keyed by FENCE height. Fixed count (15 per leaf) is applied in woodCalc.js.
export const WOOD_PADDOCK_GATE_PICKET_SKU = {
  '4': '145PT', // 1x4x5 — for 4' paddock fence gate
  '5': '146PT', // 1x4x6 — for 5' paddock fence gate
};

// --- Lattice ---
export const WOOD_LATTICE_PANEL_SKU = 'LPANEL'; // Lattice panel 12" x 96"

// A lattice job also changes the board take-off: the picket is forced to
// the 1x4x5 regardless of fence height, and 2x 1x4x8 are added per section.
// Both are existing parts, re-exported here so woodCalc.js can read the
// lattice rule from one place.
export const WOOD_LATTICE_PICKET_SKU = '145PT';       // forced picket = 1x4x5
export const WOOD_LATTICE_BOARD_SKU = '148PT';        // 1x4x8, 2 per section
